import { supabase } from '@/integrations/supabase/client';

// API REST do fluxo de troca de carteira (Busca Lead 360 + aba Aprovações).
// Backend: busca360.routes.ts, solicitacaoAvaliacao.routes.ts, transferencias.routes.ts.

const BASE = () => (import.meta.env.VITE_SUPABASE_URL as string) ?? 'https://beta.crm-api.primetecia.com.br/api';

async function authFetch(pathV1: string, init?: { method?: string; body?: any; timeoutMs?: number }): Promise<any> {
  const { data: { session } } = await supabase.auth.getSession();
  if (!session?.access_token) throw new Error('Sessão expirada — faça login novamente');
  const res = await fetch(`${BASE()}${pathV1}`, {
    method: init?.method ?? 'GET',
    signal: init?.timeoutMs ? AbortSignal.timeout(init.timeoutMs) : undefined,
    headers: {
      'Authorization': `Bearer ${session.access_token}`,
      'Content-Type': 'application/json',
    },
    body: init?.body !== undefined ? JSON.stringify(init.body) : undefined,
  });
  const json = await res.json().catch(() => ({}));
  if (!res.ok) {
    const err: any = new Error(json?.error || `Erro ${res.status}`);
    err.codigo = json?.codigo;
    err.status = res.status;
    throw err;
  }
  return json && typeof json === 'object' && 'data' in json ? json.data : json;
}

export interface Busca360Lead {
  id: string;
  status_lead: string | null;
  tipo_lead: string | null;
  consultor_nome: string | null;
  updated_at: string | null;
  proposta_composicao: string | null;
  produto: string | null;
  mes_referencia: string | null;
  valor_fatura: number | null;
  qtd_linhas_voz: number | null;
  proposta_gb: string | null;
  msg_email: string | null;
  msg_whats: string | null;
}

export interface Busca360VivoLinha {
  telefone: string | null;
  plano: string | null;
  valor: number | null;
  gb: number | null;
  m: number | null;
  detalhe?: string | null;
}

export interface Busca360Vivo {
  fonte: 'dossie' | 'parque';
  total_linhas: number;
  valor_total: number | null;
  gb_total: number | null;
  atualizado_em: string | null;
  linhas: Busca360VivoLinha[];
  fixo: Busca360VivoLinha[];
  fixo_total: number;
}

export interface Busca360PlanilhaLinha {
  numero_pedido: string | null;
  quadro: string | null;
  coluna: string | null;
  produto: string | null;
  consultor: string | null;
  qtd: number;
}

export interface Busca360Resultado {
  cnpj: string;
  cnpj_fmt: string;
  cliente_id: string | null;
  razao_social: string | null;
  fantasia: string | null;
  situacao: 'SUA_CARTEIRA' | 'DISPONIVEL' | 'SOLICITAVEL' | 'BLOQUEADO_PEDIDO';
  /** ⛔ LEI DOS 3 DONOS: o quanto o CLIENTE é protegido. Soma a `situacao`, não substitui. */
  nivel_dono?: 'DONO_CARTEIRA' | 'DONO_NEGOCIO' | 'DONO_LEAD' | null;
  dono: { id: string; nome: string | null; equipe_id: string | null; equipe: string | null; ativo: boolean | null } | null;
  leads: Busca360Lead[];
  complementar: {
    nome_adm: string | null;
    cidade: string | null;
    uf: string | null;
    linhas: number | null;
    meses_contrato: number | null;
    socios: string[];
    telefones: { numero: string; tipo: string; whatsapp: boolean | null }[];
    emails: string[];
  } | null;
  linhas_operadora: { telefone: string; operadora: string | null; tipo_linha: string | null; fonte: string | null }[];
  /** ⛔ ACHADO de terceiro (Assertiva) — nunca contato cadastrado. Ver `complementar` pra isso. */
  contatos_assertiva: { email: string; nome: string | null; decisor: boolean }[];
  planilha: Busca360PlanilhaLinha[];
  itens_cancelados: number;
  vivo: Busca360Vivo | null;
  pedidos: {
    em_andamento: number;
    ativos: number;
    pedido_bloqueio: { numero: string; consultor: string | null } | null;
  };
}

export async function busca360(q: string): Promise<{ resultados: Busca360Resultado[]; total: number; caller_gestor: boolean; truncado: boolean }> {
  return authFetch(`/v1/busca-360?q=${encodeURIComponent(q)}`);
}

export interface BuscaCarteiraLinha {
  cnpj: string;
  cnpj_fmt: string;
  razao_social: string | null;
  uf: string | null;
  tm: number | null;
  linhas_voz: number | null;
  linhas_bl: number | null;
  classificacao: string | null;
  segmento: string | null;
  cliente_id: string | null;
  dono_id: string | null;
  dono_nome: string | null;
  equipe_id: string | null;
  equipe_nome: string | null;
  dono_ativo: boolean | null;
}

export async function buscaCarteira(q: string): Promise<{ resultados: BuscaCarteiraLinha[]; total: number }> {
  return authFetch(`/v1/busca-360/carteira?q=${encodeURIComponent(q)}`);
}

export interface SolicitacaoTransferencia {
  id: string;
  cliente_id: string;
  solicitante_id: string;
  justificativa: string;
  status: string;
  resposta: string | null;
  created_at: string;
  decided_at: string | null;
  supervisor_id: string | null;
  justificativa_supervisor: string | null;
  supervisor_em: string | null;
  dono_anterior_id: string | null;
  dono_anterior_nome?: string | null;
  supervisor_nome?: string | null;
  /** DONO_CARTEIRA | DONO_NEGOCIO | DONO_LEAD — vem de fn_nivel_dono no backend.
   *  Nao se deduz do status: DONO_LEAD tambem cai em pendente_gerente quando a
   *  equipe do solicitante nao tem supervisor. */
  nivel_dono?: string | null;
  cross_equipe: boolean;
  equipe_solicitante_id: string | null;
  origem: string | null;
  clientes?: { razao_social: string | null; cnpj: string | null } | null;
  usuarios_solicitacao_avaliacao_solicitante_idTousuarios?: { nome: string | null; equipe_id: string | null; foto_url?: string | null } | null;
  usuarios_solicitacao_avaliacao_avaliador_idTousuarios?: { nome: string | null } | null;
}

export async function criarSolicitacao(params: { clienteId: string; justificativa: string; origem?: string }): Promise<SolicitacaoTransferencia | null> {
  return authFetch('/v1/solicitacoes', { method: 'POST', body: params });
}

export type ConsultaSolicitacoes = { status?: 'todos' | 'pendentes' | 'aprovado' | 'negado'; pagina?: number; id?: string; clientes?: string };
export const rotuloSolicitacao = (status: string) => ({
  pendente: 'Aguardando gerência', pendente_supervisor: 'Aguardando supervisor',
  pendente_gerente: 'Aguardando gerência', pendente_master: 'Aguardando master',
  aprovado: 'Aprovada', negado: 'Negada',
}[status] ?? status);
function parametrosSolicitacoes(opcoes: ConsultaSolicitacoes) {
  const params = new URLSearchParams();
  Object.entries(opcoes).forEach(([k, v]) => { if (v !== undefined) params.set(k, String(v)); });
  return params.toString();
}
export async function minhasSolicitacoes(opcoes: ConsultaSolicitacoes = {}): Promise<SolicitacaoTransferencia[]> {
  const data = await authFetch(`/v1/solicitacoes/minhas?${parametrosSolicitacoes(opcoes)}`);
  return Array.isArray(data) ? data : [];
}
export async function solicitacoesPendentes(opcoes: ConsultaSolicitacoes = {}): Promise<SolicitacaoTransferencia[]> {
  const data = await authFetch(`/v1/solicitacoes/pendentes?${parametrosSolicitacoes(opcoes)}`);
  return Array.isArray(data) ? data : [];
}

export async function solicitacaoAbertaDoCliente(clienteId: string): Promise<SolicitacaoTransferencia | null> {
  return authFetch(`/v1/solicitacoes/cliente/${clienteId}/aberta`);
}

export async function decidirSupervisor(id: string, aprovar: boolean, justificativa: string) {
  return authFetch(`/v1/solicitacoes/${id}/decidir-supervisor`, { method: 'POST', body: { aprovar, justificativa }, timeoutMs: 20_000 });
}

export async function aprovarSolicitacao(id: string, resposta: string) {
  return authFetch(`/v1/solicitacoes/${id}/aprovar`, { method: 'POST', body: { resposta }, timeoutMs: 20_000 });
}

export async function negarSolicitacao(id: string, resposta: string) {
  return authFetch(`/v1/solicitacoes/${id}/negar`, { method: 'POST', body: { resposta }, timeoutMs: 20_000 });
}

export async function decidirSolicitacoesLote(ids: string[], acao: 'aprovar' | 'negar', resposta: string) {
  return authFetch('/v1/solicitacoes/decidir-lote', {
    method: 'POST', body: { ids, acao, resposta }, timeoutMs: 120_000,
  }) as Promise<{ quantidade: number; ids: string[] }>;
}

export async function apropriarOrfao(clienteId: string, consultorId: string): Promise<{ ok: boolean; reason?: string }> {
  return authFetch('/v1/carteira/apropriar-orfao', { method: 'POST', body: { cliente_id: clienteId, consultor_id: consultorId } });
}

export async function transferirComercial(params: {
  clienteId?: string | null;
  cnpj?: string | null;
  razaoSocial?: string | null;
  novoConsultorId: string;
}): Promise<{ ok: boolean; clienteId: string }> {
  return authFetch('/v1/transferencias/comercial', { method: 'POST', body: params });
}

export interface HistoricoTransferencia {
  id: string;
  created_at: string;
  tipo_movimento: string | null;
  funcao: string | null;
  motivo: string | null;
  observacao: string | null;
  cliente_id: string;
  razao_social: string | null;
  cnpj: string | null;
  de_nome: string | null;
  para_nome: string | null;
  movido_por_nome: string | null;
  de_equipe_nome: string | null;
  para_equipe_nome: string | null;
  /** O novo dono vendeu DEPOIS de receber? E a prova de que o pedido de troca virou venda. */
  vendeu_depois?: boolean;
}

export async function historicoTransferencias(params: {
  cnpj?: string;
  consultorId?: string;
  tipo?: string;
  limit?: number;
  offset?: number;
} = {}): Promise<HistoricoTransferencia[]> {
  const qs = new URLSearchParams();
  if (params.cnpj) qs.set('cnpj', params.cnpj);
  if (params.consultorId) qs.set('consultorId', params.consultorId);
  if (params.tipo) qs.set('tipo', params.tipo);
  if (params.limit) qs.set('limit', String(params.limit));
  if (params.offset) qs.set('offset', String(params.offset));
  const data = await authFetch(`/v1/transferencias/historico?${qs.toString()}`);
  return Array.isArray(data) ? data : [];
}

export interface TrocaCarteiraRegistro {
  chave: string; cnpj: string; razao_social: string | null; uf: string | null;
  origem: 'saida' | 'liberacao'; semana_saida: string | null; data_liberacao: string | null;
  parceiro: string; conferencia: 'a_conferir' | 'saida_confirmada' | 'saida_nao_confirmada';
  liberacao: 'a_conferir' | 'sim' | 'nao'; conclusao: 'a_conferir' | 'regular' | 'indevida';
  observacao: string; responsavel_id: string | null; responsavel_nome: string | null;
  claro_status?: string | null; claro_enviado_em?: string | null;
  versao: number; presente_atual: boolean; saida_na_fonte: boolean;
  updated_at: string | null; atualizado_por: string | null;
  voz: number | null; tm: number | null; dono_nome: string | null; equipe_nome: string | null;
  situacao: TrocaCarteiraSituacao;
}
export type TrocaCarteiraSituacao = 'conferir' | 'claro' | 'regular' | 'indevida' | 'devolvida' | 'voltou' | 'liberada';
export type TrocaCarteiraFiltro = 'conferir' | 'claro' | 'conferidas' | 'voltou' | 'liberacoes' | 'todas';
export type TrocaCarteiraOrdem = 'recente' | 'linhas';
export interface TrocaCarteiraLista {
  total: number; clientes: number; linhas: number; com_5: number; ultima_base: string | null;
  contagens: Record<TrocaCarteiraFiltro, number>; bases: { semana: string; total: number }[];
  registros: TrocaCarteiraRegistro[];
}
export interface TrocaCarteiraEdicao {
  parceiro: string; conferencia: TrocaCarteiraRegistro['conferencia']; liberacao: TrocaCarteiraRegistro['liberacao'];
  conclusao: TrocaCarteiraRegistro['conclusao']; observacao: string; responsavel_id: string | null; versao: number;
}
export const listarTrocasCarteira = (p: { q: string; filtro: TrocaCarteiraFiltro; pagina: number; base: string; ordem: TrocaCarteiraOrdem }): Promise<TrocaCarteiraLista> =>
  authFetch(`/v1/carteira-claro/trocas?${new URLSearchParams({ q: p.q, filtro: p.filtro, pagina: String(p.pagina), base: p.base, ordem: p.ordem })}`);
export const opcoesTrocasCarteira = (): Promise<{ parceiros: string[]; responsaveis: { id: string; nome: string }[]; pode_liberar: boolean }> => authFetch('/v1/carteira-claro/trocas/opcoes');
export const salvarConferenciaCarteira = (chave: string, body: TrocaCarteiraEdicao): Promise<TrocaCarteiraRegistro> => authFetch(`/v1/carteira-claro/trocas/${encodeURIComponent(chave)}`, { method: 'PUT', body });
export const registrarLiberacaoCarteira = (body: { cnpj: string; parceiro: string; data_liberacao: string; observacao: string }): Promise<TrocaCarteiraRegistro> => authFetch('/v1/carteira-claro/trocas/liberacoes', { method: 'POST', body });
export const historicoTrocaCarteira = (chave: string): Promise<{ registrado_em: string; nome: string; depois: TrocaCarteiraEdicao & { evento?: string; status?: string; retorno?: string } }[]> => authFetch(`/v1/carteira-claro/trocas/${encodeURIComponent(chave)}/historico`);

export interface TimelineVinculosDados {
  cliente: { id: string; cnpj: string; razao_social: string; nivel_dono: string; dono_atual: string | null; equipe: string | null } | null;
  pedidos: { total: number; registros: { id: string; numero_pedido: string; created_at: string; status_agregado: string; consultor: string | null; pedido_mae_id: string | null; etapas: { quadro: string | null; status: string | null }[] }[] };
  conversas: { total: number; registros: { id: string; contact_name: string | null; telefone: string; status: string; last_message_at: string | null; created_at: string; responsavel: string | null }[] };
}
export const consultarVinculosTimeline = (cnpj: string, paginaPedidos: number, paginaConversas: number): Promise<TimelineVinculosDados> =>
  authFetch(`/v1/timeline/vinculos?${new URLSearchParams({cnpj,pagina_pedidos:String(paginaPedidos),pagina_conversas:String(paginaConversas)})}`);

export interface ContestacaoClaro {
 id:string; status:string; de:string; para:string; copia:string; assunto:string; texto:string; assinatura:string;
 criado_em:string; enviado_em:string|null; enviado_por_nome?:string; message_id:string|null; erro:string|null; retorno:string|null; versao:number;
}
export interface PreviaClaro {
 mensagem:{de:string;para:string;copia:string;assunto:string;texto:string;assinatura:string;assinatura_email:string;revisao:string};
 bloqueio:string|null;envios:ContestacaoClaro[];
}
export const consultarContestacaoClaro=(chave:string):Promise<PreviaClaro> => authFetch(`/v1/carteira-claro/trocas/${encodeURIComponent(chave)}/claro`);
export const enviarContestacaoClaro=(chave:string,body:{id:string;revisao:string}):Promise<ContestacaoClaro> => authFetch(`/v1/carteira-claro/trocas/${encodeURIComponent(chave)}/claro`,{method:'POST',body});
export const registrarRetornoClaro=(chave:string,body:{id:string;versao:number;status:'retorno_recebido'|'devolvido';retorno:string}):Promise<ContestacaoClaro> => authFetch(`/v1/carteira-claro/trocas/${encodeURIComponent(chave)}/claro/retorno`,{method:'PUT',body});

export function retomarSincronizacaoCarteira(id: string) {
  return authFetch(`/v1/carteira-claro/importacoes/${id}/retomar`, {method:'POST',timeoutMs:20_000});
}
